import {AttrsSchema, Component, jsx} from 'panel';
import {VNode} from 'snabbdom';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {layoutManager} from 'common/report/util/layout-manager';
import {getDownloadMode} from 'common/report/util';
import {DownloadMode} from 'common/report/constants';
import {IronContextRegistry} from 'common/report/context-registry';
import Layout from 'common/stylesheets/mixins/layout.json';
import {KEYDOWN_EVENTS, PANEL_MIN_WIDTH, SIDEBAR_DEFAULT_RATIO} from 'common/components/panel/constants';
import {PanelEl} from 'common/components/panel';
import {isChameleonAvailable, showTour, TourId} from 'common/util/chameleon';
import {PerfMeasure} from 'common/report/perf-measure';
import {currentProjectStore} from 'common/report/stores/project-details';
import {Direction} from 'common/components/panel/types';
import {useStoreAndFetchOnce} from 'common/util/component-helpers';
import {getCommandKey} from 'common/util/keyboard';

import {ContentFrame, HORIZONTAL_CONTENT_PADDING_STYLE} from '../sidenav-layout/content-frame';
import css from './index.inline.styl';

const COMPONENT_NAME = `mp-fullwidth-report`;

const {PAGE_MIN_WIDTH} = Layout;

type FullWidthReportJsxProps = {
  on?: EmptyObj;
  attrs?: FullWidthReportAttrs;
};
export interface FullWidthReportAttrs {
  'resizing-layout-mode'?: boolean;
}

/**
 * Layout component for a full width report
 * Provides a sidebar and report content sections both independently scrollable
 *
 * @TODO [pablo] make this component query builder aware by rendering report specific
 * components (report header or qb) by itself taking a config object
 */
export class FullWidthReport extends Component<void, FullWidthReportAttrs, EmptyObj, EmptyObj, IronContextRegistry> {
  static get attrsSchema(): AttrsSchema<FullWidthReportAttrs> {
    return {
      'resizing-layout-mode': `boolean`,
    };
  }

  static canUseResizingLayout() {
    return true;
  }

  get config() {
    return {
      css,
      useShadowDom: true,
      contexts: [`reportLayoutManager` as const, `urlManager` as const],
      template: () => this.renderTemplate(),
    };
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  renderTemplate() {
    const isDownloadMode = getDownloadMode(this.getContext(`urlManager`)) !== DownloadMode.None;

    const minContentWidth = PAGE_MIN_WIDTH - PANEL_MIN_WIDTH;

    return (
      <div
        sel='.fullwidth-report-container'
        class={{
          'download-mode': isDownloadMode,
        }}
      >
        <slot attrs={{name: `report-header`}} />
        <div sel='.fullwidth-report'>
          <PanelEl
            sel='.sidebar'
            attrs={{
              'storage-key': `query-builder`,
              'min-adjacent-size': minContentWidth,
              'show-collapse-button': true,
              'panel-name': `Query Panel`,
              'resize-direction': Direction.Right,
              ...(FullWidthReport.canUseResizingLayout()
                ? {'collapse-key': KEYDOWN_EVENTS.Digit1}
                : {'collapse-key-modifier': getCommandKey(), 'collapse-key': KEYDOWN_EVENTS.Backslash}),
              'default-ratio': SIDEBAR_DEFAULT_RATIO,
            }}
          >
            <slot attrs={{name: `query`}} />
          </PanelEl>
          <ContentFrame
            sel={`.right-panel`}
            contentFrameClass={{'resizing-layout': this.attr(`resizing-layout-mode`)}}
            isDownloadMode={isDownloadMode}
            minContentWidth={this.attr(`resizing-layout-mode`) ? null : minContentWidth}
            includePadding={!this.attr(`resizing-layout-mode`)}
            isReportScrollContainer={true}
          >
            <div
              sel='.banner-and-report-controls-container'
              style={{
                padding: this.attr(`resizing-layout-mode`)
                  ? `0 ${HORIZONTAL_CONTENT_PADDING_STYLE} 0 ${HORIZONTAL_CONTENT_PADDING_STYLE}`
                  : null,
              }}
            >
              <div sel='.banner-container' hook={{insert: (vnode) => this.onBannerContainerInserted(vnode)}} />
            </div>

            <div sel='.report-container'>
              <slot attrs={{name: `report`}} />
            </div>
          </ContentFrame>
        </div>
      </div>
    );
  }

  onBannerContainerInserted(vnode: VNode) {
    layoutManager.setConfig({
      bannerContainer: vnode.elm as HTMLElement,
    });
  }

  constructor() {
    super();
    const {whiteBackground} = layoutManager.getConfig();
    layoutManager.setConfig({whiteBackground: true});
    this.onDisconnected(() => layoutManager.setConfig({whiteBackground}));
  }

  connectedCallback() {
    const renderMeasure = new PerfMeasure(`render-report-container`).start();
    super.connectedCallback();
    useStoreAndFetchOnce(this, currentProjectStore);
    requestAnimationFrame(() => renderMeasure.end());
  }

  handleLearnMoreClick = (ev: MouseEvent) => {
    if (isChameleonAvailable()) {
      ev.preventDefault();
      showTour(TourId.SideQueryPanel);
    }
  };
}

export const FullWidthReportEl = defineAndMakeJsxFactory<FullWidthReportJsxProps>(COMPONENT_NAME, FullWidthReport);
export const FullWidthReportReact = panel2react<FullWidthReportJsxProps>(COMPONENT_NAME, FullWidthReport);
